import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';

const AgentActivitySummaryReportPage = ({ dateRangeState, selectedTeams = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = dateRangeState.currentStart.format('YYYY-MM-DD');
        const endDate = dateRangeState.currentEnd.format('YYYY-MM-DD');

        const requestData = {
          date_range: {
            start_date: startDate,
            end_date: endDate,
          },
        };

        if (selectedTeams && selectedTeams.length > 0) {
          requestData.team_filter = selectedTeams;
        }

        const response = await api.post('/report/agent/activity-summary/', requestData);
        const data = response.data || {};
        setRows(data.results || []);
      } catch (error) {
        console.error('Error fetching agent activity summary:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRangeState, selectedTeams]);

  return (
    <Box sx={{ flexGrow: 1, pt: 0, pb: 3 }}>
      <Box
        sx={{
          mb: 2,
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: 'text.primary', mb: 0.5 }}
          >
            Agent Activity Summary Report
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
            Overview of agent activities across calls, visits and follow-ups.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 500,
          }}
          // TODO: Wire this to your export logic (CSV / XLSX / PDF)
          onClick={() => {
            console.log('Export Agent Activity Summary clicked');
          }}
        >
          Export
        </Button>
      </Box>
      <Box sx={{ px: { xs: 2, sm: 3 } }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: (theme) => theme.shadows[2],
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[50],
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Agent Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Assigned</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Active Leads</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Contacted</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Connected</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Outgoing Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Incoming Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Connected Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Talk Time</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Details Shared</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Notes Added</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Unique Leads Notes Added</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Follow-ups Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Follow-ups Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visits Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Visits Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meetings Scheduled</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meetings Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Leads Converted</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Not Interested Leads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={idx}
                    hover
                    sx={(theme) => ({
                      height: theme.spacing(7),
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '&:last-child td, &:last-child th': { border: 0 },
                    })}
                  >
                    <TableCell>{row.agentName}</TableCell>
                    <TableCell align="right">{row.leadsAssigned}</TableCell>
                    <TableCell align="right">{row.activeLeads}</TableCell>
                    <TableCell align="right">{row.uniqueLeadsContacted}</TableCell>
                    <TableCell align="right">{row.uniqueLeadsConnected}</TableCell>
                    <TableCell align="right">{row.totalCalls}</TableCell>
                    <TableCell align="right">{row.outgoingCalls}</TableCell>
                    <TableCell align="right">{row.incomingCalls}</TableCell>
                    <TableCell align="right">{row.connectedCalls}</TableCell>
                    <TableCell align="right">{row.talkTime}</TableCell>
                    <TableCell align="right">{row.detailsShared}</TableCell>
                    <TableCell align="right">{row.totalNotesAdded}</TableCell>
                    <TableCell align="right">{row.uniqueLeadsNotesAdded}</TableCell>
                    <TableCell align="right">{row.followUpsScheduled}</TableCell>
                    <TableCell align="right">{row.followUpsCompleted}</TableCell>
                    <TableCell align="right">{row.visitsScheduled}</TableCell>
                    <TableCell align="right">{row.visitsCompleted}</TableCell>
                    <TableCell align="right">{row.meetingsScheduled}</TableCell>
                    <TableCell align="right">{row.meetingsCompleted}</TableCell>
                    <TableCell align="right">{row.leadsConverted}</TableCell>
                    <TableCell align="right">{row.notInterestedLeads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default AgentActivitySummaryReportPage;


